import React from "react";
import "./OnuDetailsModal.css";

// Shows the OLT/ONU monitoring service's lookup result for a customer's
// MAC address — onu_details.php looks the MAC up from the broadband
// provider first, then queries the shared OLT/ONU service with it.
//
// Props:
//   open     — whether the modal is visible
//   onClose  — () => void
//   loading  — true while onu_details.php is in flight
//   error    — error message string, or null
//   data     — the onu_details.php success payload:
//              { mac, found, best, seen_on }
//              (onu_details.php normalizes the upstream /find/ response's
//              flat shape — olt_name, onu_interface, optical.*, etc. —
//              into this "best" object, so this component doesn't need
//              to know about the upstream API's own field names.)
const OnuDetailsModal = ({ open, onClose, loading, error, data }) => {
  if (!open) return null;

  const best = data?.best || null;
  const seenOn = Array.isArray(data?.seen_on) ? data.seen_on : [];

  const fmtDate = (value) => {
    if (!value) return "--";
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const fmtPower = (value) => {
    if (value === null || value === undefined) return "--";
    return `${value} dBm`;
  };

  // Rough, generally-accepted GPON/EPON optical power bands — not
  // provider-specific, just enough to flag an obviously bad reading at
  // a glance. Anything missing just renders neutral (no badge color).
  const powerLevel = (value) => {
    if (value === null || value === undefined) return "";
    if (value <= -27) return "power-critical";
    if (value <= -24) return "power-warning";
    return "power-good";
  };

  // data_age_seconds replaces the old boolean "stale" flag from the
  // /by-mac/ endpoint — turn it into both a human-readable age and a
  // simple "this reading is old" warning past an hour.
  const fmtAge = (seconds) => {
    if (seconds === null || seconds === undefined) return null;
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    const remMins = mins % 60;
    return remMins ? `${hrs}h ${remMins}m` : `${hrs}h`;
  };

  const isStale =
    typeof best?.data_age_seconds === "number" && best.data_age_seconds > 3600;

  return (
    <div className="onu-modal-overlay" onClick={onClose}>
      <div className="onu-modal" onClick={(e) => e.stopPropagation()}>
        <div className="onu-modal-header">
          <h3>📡 ONU Details</h3>
          <button className="onu-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="onu-modal-body">
          {loading && (
            <div className="onu-modal-state">
              <span className="onu-modal-spinner"></span>
              <p>Looking up ONU details…</p>
            </div>
          )}

          {!loading && error && (
            <div className="onu-modal-state">
              <div className="onu-modal-state-icon">⚠️</div>
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && !data?.found && (
            <div className="onu-modal-state">
              <div className="onu-modal-state-icon">🔍</div>
              <p>No ONU record found for this connection's MAC address.</p>
              {data?.mac && (
                <p className="onu-modal-mac-tried">Looked up: {data.mac}</p>
              )}
            </div>
          )}

          {!loading && !error && data?.found && best && (
            <>
              <div className="onu-modal-status-row">
                <span
                  className={`onu-status-badge ${
                    best.onu_status === "online" ? "online" : "offline"
                  }`}
                >
                  <span className="status-dot"></span>
                  {best.onu_status || "unknown"}
                </span>
                {isStale && (
                  <span className="onu-status-badge stale">
                    reading is {fmtAge(best.data_age_seconds)} old
                  </span>
                )}
              </div>

              <div className="onu-modal-grid">
                <div className="onu-modal-card">
                  <h4>OLT / Port</h4>
                  <div className="onu-modal-row">
                    <span>OLT</span>
                    <strong>{best.device_name || "--"}</strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>OLT IP</span>
                    <strong>{best.device_ip || "--"}</strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>Location</span>
                    <strong>{best.device_location || "--"}</strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>PON Port</span>
                    <strong>{best.port_name || best.pon || "--"}</strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>ONU ID</span>
                    <strong>{best.onu_id ?? "--"}</strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>ONU Type</span>
                    <strong>{best.onu_type || "--"}</strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>VLAN</span>
                    <strong>{best.vlan ?? "--"}</strong>
                  </div>
                </div>

                <div className="onu-modal-card">
                  <h4>Signal</h4>
                  <div className="onu-modal-row">
                    <span>ONU Rx Power</span>
                    <strong className={powerLevel(best.onu_rx_power)}>
                      {fmtPower(best.onu_rx_power)}
                    </strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>ONU Tx Power</span>
                    <strong>{fmtPower(best.onu_tx_power)}</strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>Rx Level</span>
                    <strong
                      className={
                        best.rx_level && best.rx_level !== "ok"
                          ? "power-warning"
                          : "power-good"
                      }
                    >
                      {best.rx_level || "--"}
                    </strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>Temperature</span>
                    <strong>
                      {best.temperature_c !== null && best.temperature_c !== undefined
                        ? `${best.temperature_c} °C`
                        : "--"}
                    </strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>Voltage</span>
                    <strong>
                      {best.voltage_v !== null && best.voltage_v !== undefined
                        ? `${best.voltage_v} V`
                        : "--"}
                    </strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>Bias Current</span>
                    <strong>
                      {best.bias_ma !== null && best.bias_ma !== undefined
                        ? `${best.bias_ma} mA`
                        : "--"}
                    </strong>
                  </div>
                </div>

                <div className="onu-modal-card">
                  <h4>MAC / Operator</h4>
                  <div className="onu-modal-row">
                    <span>ONU MAC</span>
                    <strong>{best.onu_mac || "--"}</strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>Customer MAC</span>
                    <strong>{best.customer_mac || data.mac || "--"}</strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>Operator</span>
                    <strong>
                      {best.operator_name || best.operator_phone || "--"}
                    </strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>Last Seen</span>
                    <strong>{fmtDate(best.last_seen)}</strong>
                  </div>
                </div>
              </div>

              {seenOn.length > 1 && (
                <div className="onu-modal-matches">
                  <h4>Seen on ({seenOn.length})</h4>
                  <table className="onu-modal-table">
                    <thead>
                      <tr>
                        <th>Device</th>
                        <th>Port</th>
                        <th>VLAN</th>
                        <th>Last Seen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {seenOn.map((m, i) => (
                        <tr key={i}>
                          <td>{m.device_name || m.device_ip || "--"}</td>
                          <td>{m.port || "--"}</td>
                          <td>{m.vlan ?? "--"}</td>
                          <td>{fmtDate(m.last_seen)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnuDetailsModal;
